import { useCallback, useEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n';
import { PageHeader } from '../components/layout/AppShell';
import { Button } from '../components/ui/Button';
import { SkeletonGrid } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { ListingCard } from '../components/ListingCard';
import { IconSearch, IconStore } from '../components/ui/icons';
import { listings } from '../lib/api';
import { apiErrorMessage } from '../lib/errors';
import type { AssetKind, LicenseType, ListingSummary, ListingSearchParams } from '../lib/types';

const KINDS: AssetKind[] = ['sample_pack', 'stem_pack', 'acapella_pack', 'audio'];
const LICENSES: LicenseType[] = ['royalty_free', 'exclusive', 'sync', 'wellness'];

// Camelot wheel: 1A–12B. Rendered as a compact select (24 values); the API
// filters exactly on assets.key_camelot (cut 8 populates it for real now).
const CAMELOT_KEYS: string[] = Array.from({ length: 12 }, (_, i) => i + 1).flatMap((n) => [`${n}A`, `${n}B`]);

// BPM facet presets → ?bpm_min/&bpm_max. Labels are numeric (locale-neutral).
const BPM_RANGES: { label: string; min?: number; max?: number }[] = [
  { label: '< 90', max: 89 },
  { label: '90–110', min: 90, max: 110 },
  { label: '110–130', min: 110, max: 130 },
  { label: '130–150', min: 130, max: 150 },
  { label: '150+', min: 150 },
];

export function Marketplace() {
  const { t } = useI18n();
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<'' | AssetKind>('');
  const [licenseType, setLicenseType] = useState<'' | LicenseType>('');
  const [camelotKey, setCamelotKey] = useState<string>('');
  const [bpmRange, setBpmRange] = useState<number | null>(null);
  const [sort, setSort] = useState<'relevance' | 'recent'>('recent');

  const [items, setItems] = useState<ListingSummary[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const run = useCallback(
    async (nextPage: number, append: boolean) => {
      const range = bpmRange !== null ? BPM_RANGES[bpmRange] : undefined;
      const params: ListingSearchParams = {
        q: q.trim() || undefined,
        kind: kind || undefined,
        license_type: licenseType || undefined,
        key: camelotKey || undefined,
        bpm_min: range?.min,
        bpm_max: range?.max,
        sort,
        page: nextPage,
        limit: 20,
      };
      try {
        const res = await listings.search(params);
        setItems((prev) => (append ? [...prev, ...res.items] : res.items));
        setPage(res.page);
        setHasMore(res.has_more);
        setCount((prev) => (append ? prev + res.items.length : res.items.length));
        setError(null);
      } catch (err) {
        setError(apiErrorMessage(err, t));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [q, kind, licenseType, camelotKey, bpmRange, sort, t],
  );

  // Debounced search on filter/query change.
  useEffect(() => {
    setLoading(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => void run(1, false), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, kind, licenseType, camelotKey, bpmRange, sort]);

  const chip = (active: boolean) =>
    [
      'type-label rounded-sm border px-3 py-1.5 transition-colors duration-fast ease-machine focus-ring',
      active ? 'border-accent bg-accent-tint text-accent' : 'border-border-strong text-secondary hover:text-primary',
    ].join(' ');

  return (
    <div>
      <PageHeader title={t.market.title} subtitle={t.market.subtitle} />

      {/* Search + filters */}
      <div className="mb-5 flex flex-col gap-3">
        <div className="relative">
          <span className="text-muted pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
            <IconSearch size={18} />
          </span>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t.market.searchPlaceholder}
            aria-label={t.market.searchPlaceholder}
            className="h-11 w-full rounded-sm border border-border-interactive bg-surface-2 pl-10 pr-3 font-mono text-[14px] text-primary placeholder:text-muted focus-ring focus:border-accent"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className={chip(kind === '')} onClick={() => setKind('')}>
            {t.market.filterAll}
          </button>
          {KINDS.map((k) => (
            <button key={k} type="button" className={chip(kind === k)} onClick={() => setKind(k)}>
              {k.replace('_', ' ')}
            </button>
          ))}
          <span className="mx-1 h-5 w-px bg-border-strong" aria-hidden />
          {LICENSES.map((l) => (
            <button
              key={l}
              type="button"
              className={chip(licenseType === l)}
              onClick={() => setLicenseType((cur) => (cur === l ? '' : l))}
            >
              {t.listing.license[l]}
            </button>
          ))}
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as 'relevance' | 'recent')}
            aria-label="sort"
            className="type-label ml-auto h-9 rounded-sm border border-border-interactive bg-surface-2 px-2 text-secondary focus-ring"
          >
            <option value="recent">{t.market.sortRecent}</option>
            <option value="relevance">{t.market.sortRelevance}</option>
          </select>
        </div>

        {/* bpm/key (Camelot) facet chips — cut 8 owed; API honors key/bpm_min/bpm_max */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="type-label text-muted mr-1">{t.market.filterBpm}</span>
          {BPM_RANGES.map((r, i) => (
            <button
              key={r.label}
              type="button"
              className={chip(bpmRange === i)}
              onClick={() => setBpmRange((cur) => (cur === i ? null : i))}
            >
              {r.label}
            </button>
          ))}
          <span className="mx-1 h-5 w-px bg-border-strong" aria-hidden />
          <label className="type-label text-muted" htmlFor="key-facet">
            {t.market.filterKey}
          </label>
          <select
            id="key-facet"
            value={camelotKey}
            onChange={(e) => setCamelotKey(e.target.value)}
            className="type-label h-9 rounded-sm border border-border-interactive bg-surface-2 px-2 text-secondary focus-ring"
          >
            <option value="">{t.market.keyAny}</option>
            {CAMELOT_KEYS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </div>

        {!loading ? <span className="type-data text-muted tnum">{t.market.resultCount(count)}</span> : null}
      </div>

      {error ? (
        <p className="type-body-sm text-danger mb-4" role="alert">
          {error}
        </p>
      ) : null}

      {loading ? (
        <SkeletonGrid count={8} />
      ) : items.length === 0 ? (
        <EmptyState icon={<IconStore size={48} />} title={t.market.emptyTitle} body={t.market.emptyBody} />
      ) : (
        <>
          <div
            className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(240px,1fr))]"
            aria-live="polite"
          >
            {items.map((l) => (
              <ListingCard key={l.id} listing={l} />
            ))}
          </div>
          {hasMore ? (
            <div className="mt-6 flex justify-center">
              <Button
                variant="ghost"
                size="md"
                loading={loadingMore}
                onClick={() => {
                  setLoadingMore(true);
                  void run(page + 1, true);
                }}
              >
                {t.common.loadingMore}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
